import React, { useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { PencilEdit02Icon, Book01Icon, MessageSecure01Icon } from "hugeicons-react"
import "../../Styles/Header/Header.css"
import { MenuSleekIcon } from "../../assets/CustomSvgs"
import { useAuth } from "../../contexts/AuthContext"
import { useChatContext } from "../../contexts/AppDataContext"
import { LoginButton } from "../Auth/Buttons/LoginButton"
import { useSidebar } from "../../contexts/SideBarContext"
import { useWindow } from "../../contexts/WindowContext"
import TitleHeader from "./TitleHeader"
import { HoverToolTip } from "../Layout/Items/HoverToolTip"
import { useResourceContext } from "../../contexts/ResourceContext"
import { ResourcesButton } from "../Resources/ResourcesButton"
import { useSpaceContext } from "../../contexts/AppDataContext";
import { useToast } from "../../contexts/ToastContext";
import { useInputContext } from "../../contexts/InputContext";
import { SignUpButton } from "../Auth/Buttons/SignUpButton"
import { HeaderUpgradePrompt } from "./HeaderUpgradePrompt"
import { UserAvatar } from "../Layout/MiniDisplays/UserAvatar"

interface HeaderProps {
  isSettingsPage?: boolean;
  /** Set on a shared chat: shown in place of the owner-only title menu. */
  sharedChatTitle?: string;
}

export const Header: React.FC<HeaderProps> = ({ isSettingsPage, sharedChatTitle }) => {
  const isSharedChat = sharedChatTitle !== undefined;
  const navigate = useNavigate();
  const auth  = useAuth();
  const toast = useToast();
  const { isAuthenticated, isDataLoading, isLoading} = auth;
  const {isSpaceChat, currentChatSpace, isSpaceView, setMessages, setMemoizedChatTitle, isIncognito, enterIncognito, exitIncognito, filteredMessages, currentChatId } = useChatContext();
  const { setInput, focusInput } = useInputContext();
  const { handleCreateSpaceChat, currentSpace } = useSpaceContext();
  const { toggleSidebar } = useSidebar();
  const { isResourcesVisible } = useResourceContext();
  const { windowWidth } = useWindow();
  const isMobile = windowWidth <= 768;
  const isHeaderDataLoading =
    isLoading ||
    (isAuthenticated &&
      (isDataLoading ||
        auth.isSubscriptionLoading ||
        !auth.initialPayloadLoaded));

  const handleAddChat = useCallback(async () => {
    try {
      if (isIncognito) {
        exitIncognito();
      }
      setMessages([]);
      if (!isAuthenticated) return;
      setInput('');
      setMemoizedChatTitle('New Chat');
      navigate('/', { replace: false });
      if(windowWidth > 768) {
        setTimeout(() => {
          focusInput();
        }, 100);
      }
    } catch (error) {
      toast('Failed to create a new chat. Please try again.', 'error');
    }
  }, [navigate, toast, setMessages, setInput, setMemoizedChatTitle, focusInput, windowWidth, isAuthenticated, isIncognito, exitIncognito]);

  const handleLogoClick = useCallback(() => {
    navigate('/', { replace: false });
  }, [navigate]);

  const renderLeft = () => {
    let content: React.ReactNode = null;

    if (!isAuthenticated) {
        content = (
          <div className="header-logo-container">
            <button
              type="button"
              className="header-wordmark"
              onClick={handleLogoClick}
              aria-label="StudyBuddy home"
            >
              Buddy
            </button>
          </div>
        );
    } else if (isSpaceChat && isMobile) {
      content = (
        <button
          className="space-header-button"
          onClick={() => {
            currentChatSpace?.id ? navigate(`/spaces/${currentChatSpace?.id}`) : navigate('/spaces');
          }}
          aria-label="Return to space"
          type="button"
        >
          <Book01Icon size={16} />
          <span className="space-header-title">{currentChatSpace?.title || 'Space'}</span>
          <HoverToolTip
            text="Return to Space"
            position="bottom"
          />
        </button>
      );
    } else if (isMobile) {
      content = (
        <div className="header-left-buttons">
          <button
            onClick={toggleSidebar}
            className="header-button hoverable"
            type="button"
            aria-label="Toggle sidebar"
          >
              <MenuSleekIcon width={24} height={24} color="var(--text-primary)" />
              <HoverToolTip
                text="Toggle Sidebar"
                position="bottom"
              />
          </button>
        </div>
      );
    }

    return (
      <div className="header-left">
        {content}
        {isSettingsPage && (
          <div className="header-settings-container">
            <h2 className="header-settings-title">Settings</h2>
          </div>
        )}
        {isSharedChat ? (
          <div className="title-header-container">
            <div className="chat-title-container shared">
              <h1 className="chat-title">{sharedChatTitle || 'Shared Chat'}</h1>
            </div>
          </div>
        ) : (
          <TitleHeader />
        )}
        <HeaderUpgradePrompt />
      </div>
    );
  }

  const handleToggleIncognito = useCallback(() => {
    if (isIncognito) {
      exitIncognito();
    } else {
      // Make sure the user is on the new-chat surface so the empty
      // private state renders and there is no leftover chat header.
      if (currentChatId && currentChatId !== 'new') {
        navigate('/', { replace: false });
      }
      setMessages([]);
      setMemoizedChatTitle('');
      enterIncognito();
    }
  }, [isIncognito, enterIncognito, exitIncognito, currentChatId, navigate, setMessages, setMemoizedChatTitle]);

  const showIncognitoToggle =
    isAuthenticated &&
    !isSharedChat &&
    !isSpaceView &&
    !isSpaceChat &&
    (isIncognito ||
      !currentChatId ||
      currentChatId === 'new' ||
      filteredMessages.length === 0);

  const renderIncognitoToggle = () => (
    <button
      onClick={handleToggleIncognito}
      className={`header-button hoverable incognito-toggle${isIncognito ? ' incognito-active' : ''}`}
      aria-label={isIncognito ? 'Exit private chat' : 'Start private chat'}
      type="button"
    >
      <MessageSecure01Icon size={windowWidth > 600 ? 18 : 22} />
      <span className="incognito-toggle-label">Private</span>
      <HoverToolTip
        text={isIncognito ? 'Exit Private Chat' : 'Start Private Chat'}
        position="bottom"
      />
    </button>
  );

  const renderRight = () => {
    if(isSettingsPage) {
        return (
          <div className="header-right">
            <UserAvatar />
          </div>
        );
    }
    return (
      <div className="header-right">
        {!isResourcesVisible ? (
          <>
            {isAuthenticated ? (
              <>
                {showIncognitoToggle && renderIncognitoToggle()}
                {isMobile && !showIncognitoToggle && (
                  <>
                    {isSpaceView && currentSpace ? (
                      <button
                        className="return-to-chats"
                        data-header-link="true"
                        onClick={() => navigate("/spaces")}
                        type="button"
                      >
                        <Book01Icon size={18} />
                        <span>Spaces</span>
                      </button>
                    ) : !isSpaceChat && !isSpaceView ? (
                      <button onClick={handleAddChat} className="header-button hoverable" type="button" aria-label="Start new chat">
                        <PencilEdit02Icon size={20} />
                        <HoverToolTip text="New Chat" position="bottom" />
                      </button>
                    ) : isSpaceChat ? (
                      <button onClick={() => handleCreateSpaceChat(true)} className="header-button hoverable" type="button" aria-label="Start new space chat">
                        <PencilEdit02Icon size={20} />
                        <HoverToolTip text="New Space Chat" position="bottom" />
                      </button>
                    ) : null}
                  </>
                )}
              </>
            ) : (
              <>
              
                <LoginButton />
                {windowWidth > 768 && <SignUpButton minimize={true} /> }
              </>
            )}
            <ResourcesButton />
          </>
        ) : null}
      </div>
    );
  }

  if(windowWidth > 768 && isSpaceView) {
    return null;
  }

  if(isHeaderDataLoading) {
    return <div className="chat-header" aria-busy="true"></div>;
  }

  return (
    <div className="chat-header">
      {renderLeft()}
      {renderRight()}
    </div>
  );
};
